import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import sessionManager from './utils/sessionManager';
import API_CONFIG from './config';
import SiteHeader from './components/SiteHeader';
import './CreditReportPage.css';

const CreditReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creditData, setCreditData] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: true,
    scores: true,
    tradelines: true,
    publicRecords: true,
    inquiries: true,
    addresses: true
  });

  // Get credit report owner's name from URL parameters
  const getCreditReportOwnerName = () => {
    const tenantName = searchParams.get('tenant');
    const sessionData = sessionManager.getUserInfo();
    
    // If viewing another user's credit report (agent/landlord viewing tenant's)
    if (tenantName) {
      return tenantName;
    }
    
    // If viewing own credit report
    return sessionData?.firstName || 'your';
  };

  // Parse the Equifax OneView data structure
  const parsed = useMemo(() => {
    if (!creditData) return null;
    return normalizeEquifax(creditData);
  }, [creditData]);



  // Load credit report data from secure API
  const loadCreditReport = async () => {
    try {
      const sessionData = sessionManager.getUserInfo();
      if (!sessionData?.authToken) {
        alert('⚠️ Session expired. Please log in again.');
        navigate('/');
        return;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREDIT_REPORT_GET}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionData.authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Credit report response:', data);
      console.log('Credit report data structure:', data.data);
      
      if (!response.ok || !data.success) {
        if (data.error === 'No credit report found') {
          alert('⚠️ No credit report found. Please pull a credit report first.');
          navigate('/profile');
        } else if (data.error?.includes('Password required for credit report')) {
          alert('⚠️ Please set up a password in your account settings to view credit reports.');
          navigate('/profile');
        } else {
          alert(`❌ Error loading credit report: ${data.error || 'Unknown error'}`);
          navigate('/profile');
        }
        return;
      }

      setCreditData(data.data);
    } catch (error) {
      console.error('Error loading credit report:', error);
      alert('❌ Network error loading credit report. Please try again.');
      navigate('/profile');
      return;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check authentication
    const authenticated = sessionManager.isAuthenticated();
    if (!authenticated) {
      navigate('/');
      return;
    }
    
    // Load credit report immediately (no password prompt needed)
    loadCreditReport();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getCreditScoreColor = (score) => {
    if (score >= 800) return '#10b981'; // Excellent - Green
    if (score >= 740) return '#84cc16'; // Very Good - Light Green
    if (score >= 670) return '#eab308'; // Good - Yellow
    if (score >= 580) return '#f97316'; // Fair - Orange
    return '#ef4444'; // Poor - Red
  };

  const getCreditScoreLabel = (score) => {
    if (score >= 800) return 'Excellent';
    if (score >= 740) return 'Very Good';
    if (score >= 670) return 'Good';
    if (score >= 580) return 'Fair';
    return 'Poor';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };



  if (loading) {
    const ownerName = getCreditReportOwnerName();
    const loadingText = ownerName === 'your' 
      ? 'Loading your credit report...' 
      : `Loading ${ownerName}'s credit report...`;
    
    return (
      <div className="credit-report-page">
        <div className="credit-loading">
          <div className="loading-spinner"></div>
          <p>{loadingText}</p>
        </div>
      </div>
    );
  }

  if (!parsed) {
    return (
      <div className="credit-report-page">
        <SiteHeader />
        <div className="credit-container">
          <div className="error-message">
            <h2>Unable to Load Credit Report</h2>
            <p>There was an issue parsing your credit report data. Please try pulling a new report.</p>
            <button className="primary-btn" onClick={() => navigate('/credit-pull')}>
              Pull New Credit Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { consumer, scores, trades, inquiries, addresses, alerts, meta } = parsed;

  // Calculate metrics
  const primaryScore = scores && scores.length > 0 ? scores[0] : null;
  const creditScore = primaryScore ? primaryScore.score : 0;
  
  const totalDebt = trades.reduce((sum, trade) => sum + (trade.actualPayment || 0), 0);
  const totalCredit = trades.reduce((sum, trade) => sum + (trade.highCredit || 0), 0);
  const utilizationRatio = totalCredit > 0 ? ((totalDebt / totalCredit) * 100).toFixed(1) : 0;
  
  const openAccounts = trades.filter(t => t.status && !t.status.toLowerCase().includes('closed')).length;
  const closedAccounts = trades.filter(t => t.status && t.status.toLowerCase().includes('closed')).length;
  const derogatoryMarks = trades.filter(t => t.statusCode && t.statusCode >= 5).length;
  const hardInquiries = inquiries.filter(i => i.type !== 'soft').length;

  return (
    <div className="credit-report-page">
      <SiteHeader />

      <div className="credit-container">
        {/* Page Title */}
        <div className="page-title">
          <h1>Credit Report</h1>
          <p>Generated on {meta.reportDate || formatDate(new Date().toISOString())}</p>
        </div>

        {/* Navigation Tabs */}
        <div className="credit-nav">
          <button 
            className={`nav-tab ${activeSection === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveSection('overview')}
          >
            Overview
          </button>
          <button 
            className={`nav-tab ${activeSection === 'details' ? 'active' : ''}`}
            onClick={() => setActiveSection('details')}
          >
            Detailed Report
          </button>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="overview-section">
            {/* Credit Score Hero */}
            <div className="score-hero">
              <div className="score-visual">
                <div 
                  className="score-circle"
                  style={{ borderColor: getCreditScoreColor(creditScore) }}
                >
                  <span className="score-number" style={{ color: getCreditScoreColor(creditScore) }}>
                    {creditScore}
                  </span>
                  <span className="score-label">{getCreditScoreLabel(creditScore)}</span>
                </div>
              </div>
              <div className="score-details">
                <h2>Your Credit Score</h2>
                <p>Based on Equifax data</p>
                {primaryScore && (
                  <div className="score-range">
                    <span>Range: {primaryScore.range.low} - {primaryScore.range.high}</span>
                    <span>Model: {primaryScore.model}</span>
                  </div>
                )}
                <div className="score-scale">
                  <div className="scale-bar">
                    <div className="scale-poor">300-579</div>
                    <div className="scale-fair">580-669</div>
                    <div className="scale-good">670-739</div>
                    <div className="scale-very-good">740-799</div>
                    <div className="scale-excellent">800-850</div>
                  </div>
                  <div 
                    className="score-indicator"
                    style={{ 
                      left: `${((creditScore - 300) / 550) * 100}%`,
                      backgroundColor: getCreditScoreColor(creditScore)
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">💳</div>
                <div className="metric-content">
                  <h3>Total Debt</h3>
                  <span className="metric-value">{formatCurrency(totalDebt)}</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">💰</div>
                <div className="metric-content">
                  <h3>Total Credit</h3>
                  <span className="metric-value">{formatCurrency(totalCredit)}</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📊</div>
                <div className="metric-content">
                  <h3>Credit Utilization</h3>
                  <span className="metric-value">{utilizationRatio}%</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <div className="metric-content">
                  <h3>Payment History</h3>
                  <span className={`metric-value ${derogatoryMarks === 0 ? 'good' : 'warning'}`}>
                    {derogatoryMarks === 0 ? 'Excellent' : `${derogatoryMarks} Issues`}
                  </span>
                </div>
              </div>
            </div>

            {/* Account Summary */}
            <div className="summary-cards">
              <div className="summary-card">
                <h3>Account Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-number">{openAccounts}</span>
                    <span className="summary-label">Open Accounts</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-number">{closedAccounts}</span>
                    <span className="summary-label">Closed Accounts</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-number">{derogatoryMarks}</span>
                    <span className="summary-label">Derogatory Marks</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-number">{hardInquiries}</span>
                    <span className="summary-label">Hard Inquiries</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details Section */}
        {activeSection === 'details' && (
          <div className="details-section">
            {/* Personal Information */}
            <div className="detail-card">
              <div className="card-header" onClick={() => toggleSection('personalInfo')}>
                <h3>👤 Personal Information</h3>
                <span className={`expand-icon ${expandedSections.personalInfo ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.personalInfo && (
                <div className="card-content">
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Name</span>
                      <span className="info-value">{consumer.name || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Date of Birth</span>
                      <span className="info-value">{consumer.birthDate || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">SSN</span>
                      <span className="info-value">{consumer.ssn || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Current Address</span>
                      <span className="info-value">
                        {addresses.length > 0 && addresses[0].line1 ? 
                          `${addresses[0].line1}, ${addresses[0].city}, ${addresses[0].state} ${addresses[0].zip}` : 
                          'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Credit Scores */}
            <div className="detail-card">
              <div className="card-header" onClick={() => toggleSection('scores')}>
                <h3>📊 Credit Scores</h3>
                <span className={`expand-icon ${expandedSections.scores ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.scores && (
                <div className="card-content">
                  <div className="scores-grid">
                    {scores.length > 0 ? scores.map((score, index) => (
                      <div key={index} className="score-card">
                        <div className="score-model">Model {score.model}</div>
                        <div className="score-number" style={{ color: getCreditScoreColor(score.score) }}>
                          {score.score}
                        </div>
                        <div className="score-range">Range: {score.range.low} - {score.range.high}</div>
                        <div className="score-reasons">
                          Reason codes: {score.reasons?.join(', ') || 'N/A'}
                        </div>
                      </div>
                    )) : (
                      <p className="no-data">No credit scores available</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tradelines */}
            <div className="detail-card">
              <div className="card-header" onClick={() => toggleSection('tradelines')}>
                <h3>🏦 Credit Accounts</h3>
                <span className={`expand-icon ${expandedSections.tradelines ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.tradelines && (
                <div className="card-content">
                  {trades.length > 0 ? (
                    <div className="tradelines-grid">
                      {trades.map((trade, index) => (
                        <div key={index} className="tradeline-card">
                          <div className="tradeline-header">
                            <h4>{trade.creditor || `Account ${index + 1}`}</h4>
                            <span className={`status-badge ${trade.status?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}>
                              {trade.status || 'Unknown'}
                            </span>
                          </div>
                          <div className="tradeline-details">
                            <div className="detail-row">
                              <span>Type:</span>
                              <span>{trade.type || trade.portfolio || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                              <span>Account #:</span>
                              <span>{trade.accountNumber || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                              <span>High Credit:</span>
                              <span>{formatCurrency(trade.highCredit)}</span>
                            </div>
                            <div className="detail-row">
                              <span>Current Balance:</span>
                              <span>{formatCurrency(trade.actualPayment)}</span>
                            </div>
                            <div className="detail-row">
                              <span>Opened:</span>
                              <span>{trade.opened || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                              <span>Last Reported:</span>
                              <span>{trade.reported || 'N/A'}</span>
                            </div>
                            {trade.counters && (
                              <div className="detail-row">
                                <span>Late Payments:</span>
                                <span className={trade.counters.thirty > 0 || trade.counters.sixty > 0 || trade.counters.ninety > 0 ? 'warning' : 'good'}>
                                  30d: {trade.counters.thirty || 0}, 60d: {trade.counters.sixty || 0}, 90d: {trade.counters.ninety || 0}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">No credit accounts found</p>
                  )}
                </div>
              )}
            </div>

            {/* Credit Inquiries */}
            <div className="detail-card">
              <div className="card-header" onClick={() => toggleSection('inquiries')}>
                <h3>🔍 Credit Inquiries</h3>
                <span className={`expand-icon ${expandedSections.inquiries ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.inquiries && (
                <div className="card-content">
                  {inquiries.length > 0 ? (
                    <div className="inquiries-list">
                      {inquiries.map((inquiry, index) => (
                        <div key={index} className="inquiry-item">
                          <div className="inquiry-info">
                            <h4>{inquiry.creditor || 'Unknown Creditor'}</h4>
                            <p>{inquiry.date || 'N/A'}</p>
                          </div>
                          <span className={`inquiry-type ${inquiry.type === 'soft' ? 'soft-inquiry' : 'hard-inquiry'}`}>
                            {inquiry.type ? inquiry.type.charAt(0).toUpperCase() + inquiry.type.slice(1) : 'Hard'} Inquiry
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">No recent inquiries</p>
                  )}
                </div>
              )}
            </div>

            {/* Public Records & Alerts */}
            <div className="detail-card">
              <div className="card-header" onClick={() => toggleSection('publicRecords')}>
                <h3>⚠️ Public Records & Alerts</h3>
                <span className={`expand-icon ${expandedSections.publicRecords ? 'expanded' : ''}`}>▼</span>
              </div>
              {expandedSections.publicRecords && (
                <div className="card-content">
                  {(!alerts.ofac || alerts.ofac.length === 0) && (!alerts.idScan || alerts.idScan.length === 0) && (!alerts.ssn || alerts.ssn.length === 0) ? (
                    <p className="no-data">No public records or alerts found</p>
                  ) : (
                    <div className="alerts-list">
                      {alerts.ofac.map((alert, index) => (
                        <div key={`ofac-${index}`} className="alert-item ofac">
                          <h4>OFAC Alert: {alert.code}</h4>
                          <p>{alert.text}</p>
                          <span className="alert-time">{alert.time}</span>
                        </div>
                      ))}
                      {alerts.idScan.map((alert, index) => (
                        <div key={`id-${index}`} className="alert-item id-scan">
                          <h4>ID Scan: {alert.code}</h4>
                          <p>{alert.text}</p>
                        </div>
                      ))}
                      {alerts.ssn.map((alert, index) => (
                        <div key={`ssn-${index}`} className="alert-item ssn">
                          <h4>SSN Alert: {alert.code}</h4>
                          <p>{alert.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="credit-footer">
          <p>This report is generated from Equifax data and is for informational purposes only.</p>
          <p>Report generated by RentalHelp.ai • Powered by Equifax</p>
        </div>
      </div>
    </div>
  );
};

// All the utility functions from the original component
function safe(val, fallback) {
  return (val === undefined || val === null || (typeof val === "number" && Number.isNaN(val))) ? fallback : val;
}

function cleanStr(s) {
  if (s === undefined || s === null) return "";
  return String(s).toString();
}

function parseDate(raw) {
  if (!raw && raw !== 0) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // ISO-like
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    return formatDateHelper(d);
  }
  // YYYYMMDD or YYYYMM
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.slice(0,4));
    const m = Number(s.slice(4,6)) - 1;
    let d = Number(s.slice(6,8));
    if (d === 0) d = 15; // replace 00 day with mid-month
    return formatDateHelper(new Date(y, m, d));
  }
  if (/^\d{6}$/.test(s)) {
    const y = Number(s.slice(0,4));
    const m = Number(s.slice(4,6)) - 1;
    return formatMonth(new Date(y, m, 1));
  }
  // MMDDYYYY without slashes (Equifax style: 06102021), or MM00YYYY (e.g., 04002020)
  if (/^\d{8}$/.test(s)) {
    const mm = Number(s.slice(0,2));
    let dd = Number(s.slice(2,4));
    const yyyy = Number(s.slice(4,8));
    if (dd === 0) dd = 15; // handle 00-day edge case
    return formatDateHelper(new Date(yyyy, mm-1, dd));
  }

  // fallback
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : formatDateHelper(d);
}

function formatDateHelper(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
}

function formatMonth(d) {
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

function maskSSN(n) {
  if (!n && n !== 0) return "";
  const s = String(n).replace(/\D/g,"");
  return s.length === 9 ? `***-**-${s.slice(-4)}` : s.replace(/.(?=.{4})/g, "*");
}

// Main normalizer function to convert Equifax OneView JSON to structured data
function normalizeEquifax(root) {
  try {
    console.log('normalizeEquifax input:', root);
    
    // Try different possible data structures:
    // 1. Direct Equifax response: root.consumers.equifaxUSConsumerCreditReport[0]
    // 2. Wrapped in data layer: root.data.consumers.equifaxUSConsumerCreditReport[0]
    // 3. Wrapped in extra data layer: root.data.data.consumers.equifaxUSConsumerCreditReport[0]
    let report = root?.consumers?.equifaxUSConsumerCreditReport?.[0];
    
    if (!report && root?.data?.consumers?.equifaxUSConsumerCreditReport) {
      console.log('Trying data.consumers structure...');
      report = root.data.consumers.equifaxUSConsumerCreditReport[0];
    }
    
    if (!report && root?.data?.data?.consumers?.equifaxUSConsumerCreditReport) {
      console.log('Trying nested data.data.consumers structure...');
      report = root.data.data.consumers.equifaxUSConsumerCreditReport[0];
    }
    
    console.log('Found report:', report ? 'Yes' : 'No');
    if (!report) {
      console.log('Available root keys:', Object.keys(root || {}));
      console.log('Available data keys:', Object.keys(root?.data || {}));
      console.log('Available consumers keys:', Object.keys(root?.consumers || {}));
      return null;
    }

    const subject = report.subjectName || {};

    const meta = {
      identifier: report.identifier,
      reportDate: parseDate(report.reportDate),
      fileSince: parseDate(report.fileSinceDate),
      lastActivity: parseDate(report.lastActivityDate),
      customerRef: report.customerReferenceNumber,
      hitCode: report.hitCode?.description,
    };

    const consumer = {
      name: [subject.firstName, subject.middleName, subject.lastName].filter(Boolean).join(" "),
      ssn: maskSSN(report.subjectSocialNum),
      birthDate: parseDate(report.birthDate),
      ecoa: report.ECOAInquiryType,
      fraudVictim: report.fraudVictimIndicator?.description,
      nameMatch: report.nameMatchFlags,
    };

    const scores = safe(report.models, []).filter((m) => m.type === "MODEL").map((m) => ({
      model: m.modelNumber,
      score: Number(m.score),
      reasons: (m.reasons || []).map((r) => r.code),
      range: {
        low: Number(rootLowForModel(root, m.modelNumber) ?? 250),
        high: Number(rootHighForModel(root, m.modelNumber) ?? 900)
      }
    }));

    const inquiries = safe(report.inquiries, []).map((q) => ({
      date: parseDate(q.inquiryDate),
      industry: q.industryCode,
      creditor: q.customerName,
      customerNumber: q.customerNumber,
      type: q.type
    }));

    const addresses = safe(report.addresses, []).map((a) => ({
      type: a.addressType,
      line1: a.addressLine1 || [a.houseNumber, a.streetName, a.streetType].filter(Boolean).join(" "),
      city: a.cityName,
      state: a.stateAbbreviation,
      zip: a.zipCode,
      firstReported: parseDate(a.dateFirstReported),
      lastReported: parseDate(a.dateLastReported),
      source: a.sourceOfAddress?.description,
    }));

    const alerts = {
      ofac: safe(report.OFACAlerts, []).map((o) => ({
        code: o.cdcResponseCode,
        text: o.legalVerbiage,
        time: `${parseDate(o.cdcTransactionDate)} ${o.cdcTransactionTime ?? ''}`.trim(),
      })),
      idScan: safe(report.fraudIDScanAlertCodes, []).map((x) => ({ code: x.code, text: x.description })),
      ssn: safe(report.fraudSocialNumAlertCode, []).map((x) => ({ code: x.code, text: x.description })),
      contacts: safe(report.alertContacts, []).map((c) => ({
        type: c.alertType?.description,
        effective: parseDate(c.effectiveDate),
        reported: parseDate(c.dateReported),
        phones: safe(c.telephoneNumbers, []).map((t) => ({ kind: t.telephoneNumberType?.description, value: t.telephoneNumber }))
      }))
    };

    // Tradelines
    const trades = safe(report.trades, []).map((t) => ({
      creditor: t.customerName,
      customerNumber: t.customerNumber,
      accountNumber: cleanStr(t.accountNumber),
      opened: parseDate(t.dateOpened),
      reported: parseDate(t.dateReported),
      lastActivity: parseDate(t.lastActivityDate),
      designator: t.accountDesignator?.description || t.description,
      type: t.accountTypeCode?.description,
      portfolio: t.portfolioTypeCode?.description,
      status: t.rate?.description,
      statusCode: t.rate?.code,
      highCredit: t.highCredit,
      actualPayment: t.actualPaymentAmount,
      scheduledPayment: t.scheduledPaymentAmount,
      termFreq: t.termsFrequencyCode?.description || t.description,
      termDuration: t.termsDurationCode?.description,
      activity: t.activityDesignatorCode?.description,
      delinqFirstReported: parseDate(t.dateMajorDelinquencyFirstReported),
      narrative: safe(t.narrativeCodes, []).map((n) => n.description).filter(Boolean),
      history: normalizePaymentHistory(t.paymentHistory1to24 || t["24MonthPaymentHistory"]),
      counters: {
        thirty: t.thirtyDayCounter,
        sixty: t.sixtyDayCounter,
        ninety: t.ninetyDayCounter,
      }
    }));

    return {
      consumer,
      scores,
      trades,
      inquiries,
      addresses,
      alerts,
      meta,
    };
  } catch (e) {
    console.error('Error parsing Equifax data:', e);
    return null;
  }
}

function rootLowForModel(root, modelNumber) {
  // Try different possible data structures
  let report = root?.consumers?.equifaxUSConsumerCreditReport?.[0];
  if (!report) report = root?.data?.consumers?.equifaxUSConsumerCreditReport?.[0];
  if (!report) report = root?.data?.data?.consumers?.equifaxUSConsumerCreditReport?.[0];
  
  const rbp = report?.models?.find((m) => m.type === "RBP" && m.modelNumber === modelNumber);
  return rbp?.riskBasedPricingOrModel?.lowRange;
}

function rootHighForModel(root, modelNumber) {
  // Try different possible data structures
  let report = root?.consumers?.equifaxUSConsumerCreditReport?.[0];
  if (!report) report = root?.data?.consumers?.equifaxUSConsumerCreditReport?.[0];
  if (!report) report = root?.data?.data?.consumers?.equifaxUSConsumerCreditReport?.[0];
  
  const rbp = report?.models?.find((m) => m.type === "RBP" && m.modelNumber === modelNumber);
  return rbp?.riskBasedPricingOrModel?.highRange;
}

function normalizePaymentHistory(h) {
  const arr = Array.isArray(h) ? h : [];
  return arr.map((e, idx) => {
    const code = cleanStr(e?.code);
    const d = e?.description;
    return { month: `M-${arr.length - idx}`, code, desc: d };
  });
}

export default CreditReportPage;
